import React, { useEffect, useState } from 'react'
import { apiErrorMessage, getWhatsappNotificationStatus, updateWhatsappProviderSettings } from '../../../services/access'
import type { WhatsappNotificationStatus, WhatsappProviderSettingsPayload } from '../../../types/access'
import { EmptyState, ErrorBanner, LoadingState } from './AdminUiPrimitives'
import { useI18n } from '../../../i18n/I18nContext'

type WhatsappSettingsPanelProps = {
  canManageSettings: boolean
}

export const WhatsappSettingsPanel: React.FC<WhatsappSettingsPanelProps> = ({ canManageSettings }) => {
  const { t } = useI18n()
  const providerLabel = (provider?: string) => {
    if (provider === 'meta_cloud_api') return t('Meta Cloud API')
    if (provider === 'twilio') return t('Twilio')
    if (provider === 'custom') return t('Custom provider')
    return t('Disabled')
  }
  const [status, setStatus] = useState<WhatsappNotificationStatus | null>(null)
  const [form, setForm] = useState<WhatsappProviderSettingsPayload>({ provider: 'disabled', defaultLocale: 'en', enabled: false })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    if (!canManageSettings) return
    setLoading(true)
    setError('')
    try {
      const next = await getWhatsappNotificationStatus()
      setStatus(next)
      setForm({
        provider: next.providerSettings?.provider || 'disabled',
        senderPhone: next.providerSettings?.sender_phone || '',
        phoneNumberId: next.providerSettings?.phone_number_id || '',
        businessAccountId: next.providerSettings?.business_account_id || '',
        webhookVerifyTokenSet: Boolean(next.providerSettings?.webhook_verify_token_set),
        accessTokenSecretName: next.providerSettings?.access_token_secret_name || '',
        defaultLocale: next.providerSettings?.default_locale || 'en',
        enabled: Boolean(next.providerSettings?.enabled),
      })
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to load WhatsApp notification status.')))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [canManageSettings])

  const save = async () => {
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const next = await updateWhatsappProviderSettings({
        ...form,
        senderPhone: form.senderPhone || null,
        phoneNumberId: form.phoneNumberId || null,
        businessAccountId: form.businessAccountId || null,
        accessTokenSecretName: form.accessTokenSecretName || null,
        defaultLocale: form.defaultLocale || 'en',
      })
      setStatus(next)
      setNotice(t('WhatsApp settings saved. Delivery remains inactive until provider credentials, opt-ins, and approved templates are ready.'))
    } catch (err) {
      setError(apiErrorMessage(err, t('Unable to save WhatsApp settings.')))
    } finally {
      setSaving(false)
    }
  }

  if (!canManageSettings) {
    return <EmptyState title={t('WhatsApp unavailable')} description={t('Your current role does not include messaging or tenant settings access.')} />
  }
  if (loading) return <LoadingState label={t('Loading WhatsApp settings...')} />

  const counts = status?.optInCounts || {}
  const ready = Boolean(status?.deliveryReady)

  return (
    <article className="panel workspace-panel stack" data-testid="whatsapp-settings-panel">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.25rem' }}>
          <h3 style={{ margin: 0 }}>{t('WhatsApp notifications')}</h3>
          <p className="text-muted">{t('Configure tenant-level provider readiness, opt-in auditing, and template status without storing provider secrets directly.')}</p>
        </div>
        <span className={`status-pill ${ready ? 'status-pill--approved' : 'status-pill--draft'}`}>
          {ready ? t('Ready for delivery worker') : t('Not delivery-ready')}
        </span>
      </div>

      <ErrorBanner message={error} />
      {notice ? <p className="helper">{notice}</p> : null}

      <div className="admin-summary-grid">
        <div><span className="helper">{t('Provider')}</span><strong>{providerLabel(status?.providerSettings?.provider)}</strong></div>
        <div><span className="helper">{t('Opted in')}</span><strong>{Number(counts.opted_in_count || 0)}</strong></div>
        <div><span className="helper">{t('Pending opt-in')}</span><strong>{Number(counts.pending_count || 0)}</strong></div>
        <div><span className="helper">{t('Templates')}</span><strong>{status?.templates?.length || 0}</strong></div>
      </div>

      <div className="admin-form-grid">
        <label>
          <span>{t('Provider')}</span>
          <select className="input" value={form.provider || 'disabled'} onChange={(event) => setForm((current) => ({ ...current, provider: event.target.value as any }))}>
            <option value="disabled">{t('Disabled')}</option>
            <option value="twilio">{t('Twilio')}</option>
            <option value="meta_cloud_api">{t('Meta Cloud API')}</option>
            <option value="custom">{t('Custom provider')}</option>
          </select>
        </label>
        <label>
          <span>{t('Sender phone')}</span>
          <input className="input" value={form.senderPhone || ''} onChange={(event) => setForm((current) => ({ ...current, senderPhone: event.target.value }))} placeholder="+15551234567" />
        </label>
        <label>
          <span>{t('Phone number id')}</span>
          <input className="input" value={form.phoneNumberId || ''} onChange={(event) => setForm((current) => ({ ...current, phoneNumberId: event.target.value }))} />
        </label>
        <label>
          <span>{t('Business account id')}</span>
          <input className="input" value={form.businessAccountId || ''} onChange={(event) => setForm((current) => ({ ...current, businessAccountId: event.target.value }))} />
        </label>
        <label>
          <span>{t('Access token secret name')}</span>
          <input className="input" value={form.accessTokenSecretName || ''} onChange={(event) => setForm((current) => ({ ...current, accessTokenSecretName: event.target.value }))} placeholder="WHATSAPP_ACCESS_TOKEN" />
        </label>
        <label>
          <span>{t('Default locale')}</span>
          <input className="input" value={form.defaultLocale || 'en'} onChange={(event) => setForm((current) => ({ ...current, defaultLocale: event.target.value }))} />
        </label>
        <label className="checkbox-row">
          <input type="checkbox" checked={Boolean(form.webhookVerifyTokenSet)} onChange={(event) => setForm((current) => ({ ...current, webhookVerifyTokenSet: event.target.checked }))} />
          <span>{t('Webhook verify token configured outside the app')}</span>
        </label>
        <label className="checkbox-row">
          <input type="checkbox" checked={Boolean(form.enabled)} onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} />
          <span>{t('Enable WhatsApp channel after provider setup')}</span>
        </label>
      </div>

      <div className="admin-panel-toolbar">
        <p className="helper">{t('Provider failures will be logged as WhatsApp delivery attempts when a delivery worker is added; core Punch writes stay independent.')}</p>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('Saving...') : t('Save WhatsApp settings')}</button>
      </div>
    </article>
  )
}
